// Live Chat Settings — full configuration page (livechat.admin required).
// Covers: office hours, timezone, welcome/away message, max chats per agent,
// assignment mode, pre-chat fields, and post-chat rating toggle.

import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  SlidersHorizontal, Clock, MessageSquare, Users, Shuffle,
  Star, Plus, Trash2, Save, Loader2, ClipboardList,
} from "lucide-react";

// ── Types ─────────────────────────────────────────────────────────────────────

type DayWindow = {
  day: string;
  open: string;
  close: string;
  enabled: boolean;
};

type PreChatField = {
  name: string;
  label: string;
  type: "text" | "email" | "tel" | "checkbox";
  required: boolean;
  visible?: boolean;
};
const CANONICAL_FIELDS: PreChatField[] = [
  { name: "name", label: "Name", type: "text", required: true, visible: true },
  { name: "email", label: "Email", type: "email", required: true, visible: true },
  { name: "phone", label: "Phone", type: "tel", required: true, visible: true },
  { name: "countryCode", label: "Country dialling code", type: "text", required: true, visible: true },
  { name: "consent", label: "I consent to my contact details being used to respond to my enquiry.", type: "checkbox", required: true, visible: true },
];

type LiveChatSettingsDto = {
  officeHoursJson: string | null;
  timezone: string | null;
  welcomeMessage: string | null;
  awayMessage: string | null;
  maxChatsPerAgent: number;
  assignmentMode: string;
  preChatFieldsJson: string | null;
  preChatWarning?: string | null;
  enablePostChatRating: boolean;
};

const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

const DEFAULT_HOURS: DayWindow[] = DAYS.map((day) => ({
  day,
  open: "09:00",
  close: "17:00",
  enabled: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"].includes(day),
}));

const TIMEZONES = [
  "UTC",
  "America/New_York",
  "America/Chicago",
  "America/Denver",
  "America/Los_Angeles",
  "America/Anchorage",
  "America/Hawaii",
  "America/Toronto",
  "America/Vancouver",
  "America/Sao_Paulo",
  "America/Buenos_Aires",
  "America/Mexico_City",
  "Europe/London",
  "Europe/Paris",
  "Europe/Berlin",
  "Europe/Madrid",
  "Europe/Rome",
  "Europe/Amsterdam",
  "Europe/Stockholm",
  "Europe/Warsaw",
  "Europe/Istanbul",
  "Europe/Moscow",
  "Africa/Cairo",
  "Africa/Johannesburg",
  "Africa/Lagos",
  "Asia/Dubai",
  "Asia/Karachi",
  "Asia/Kolkata",
  "Asia/Dhaka",
  "Asia/Bangkok",
  "Asia/Singapore",
  "Asia/Hong_Kong",
  "Asia/Shanghai",
  "Asia/Tokyo",
  "Asia/Seoul",
  "Australia/Sydney",
  "Australia/Melbourne",
  "Pacific/Auckland",
];

const FIELD_TYPES: { value: PreChatField["type"]; label: string }[] = [
  { value: "text", label: "Text" },
  { value: "email", label: "Email" },
  { value: "tel", label: "Phone" },
  { value: "checkbox", label: "Consent" },
];

// ── Main component ─────────────────────────────────────────────────────────────

export default function LiveChatSettingsPage() {
  const { toast } = useToast();
  const qc = useQueryClient();

  const { data, isLoading } = useQuery<LiveChatSettingsDto>({
    queryKey: ["/api/live-chat/settings"],
    queryFn: () => apiRequest("GET", "/api/live-chat/settings").then((r) => r.json()),
  });

  // Local form state
  const [officeHours, setOfficeHours] = useState<DayWindow[]>(DEFAULT_HOURS);
  const [timezone, setTimezone] = useState("UTC");
  const [welcomeMessage, setWelcomeMessage] = useState("Hello! How can I help you today?");
  const [awayMessage, setAwayMessage] = useState(
    "We're currently away. Leave a message and we'll get back to you soon."
  );
  const [maxChats, setMaxChats] = useState(5);
  const [assignmentMode, setAssignmentMode] = useState("round_robin");
  const [preChatFields, setPreChatFields] = useState<PreChatField[]>([]);
  const [enablePostChatRating, setEnablePostChatRating] = useState(false);

  // Hydrate from server data when loaded
  useEffect(() => {
    if (!data) return;
    setTimezone(data.timezone ?? "UTC");
    setWelcomeMessage(data.welcomeMessage ?? "Hello! How can I help you today?");
    setAwayMessage(
      data.awayMessage ?? "We're currently away. Leave a message and we'll get back to you soon."
    );
    setMaxChats(data.maxChatsPerAgent ?? 5);
    setAssignmentMode(data.assignmentMode ?? "round_robin");
    setEnablePostChatRating(data.enablePostChatRating ?? false);
    try {
      if (data.officeHoursJson) {
        const windows = JSON.parse(data.officeHoursJson);
        if (Array.isArray(windows)) setOfficeHours(windows);
      }
    } catch { /* keep default */ }
    try {
      if (data.preChatFieldsJson) {
        const parsed = JSON.parse(data.preChatFieldsJson);
        setPreChatFields(Array.isArray(parsed) ? parsed : []);
      }
    } catch { /* keep empty */ }
  }, [data]);

  const saveMut = useMutation({
    mutationFn: () =>
      apiRequest("PATCH", "/api/live-chat/settings", {
        officeHoursJson: JSON.stringify(officeHours),
        timezone,
        welcomeMessage,
        awayMessage,
        maxChatsPerAgent: maxChats,
        assignmentMode,
        preChatFieldsJson: JSON.stringify(preChatFields),
        enablePostChatRating,
      }).then((r) => r.json()),
    onSuccess: () => {
      toast({ description: "Live chat settings saved." });
      qc.invalidateQueries({ queryKey: ["/api/live-chat/settings"] });
    },
    onError: (e: any) =>
      toast({ title: "Save failed", description: e.message, variant: "destructive" }),
  });

  // ── Office hours helpers ──
  const toggleDay = (idx: number) =>
    setOfficeHours((h) => h.map((d, i) => (i === idx ? { ...d, enabled: !d.enabled } : d)));
  const setHour = (idx: number, field: "open" | "close", val: string) =>
    setOfficeHours((h) => h.map((d, i) => (i === idx ? { ...d, [field]: val } : d)));

  // ── Pre-chat field helpers ──
  const addField = () =>
    setPreChatFields((f) => {
      const next = CANONICAL_FIELDS.find(c => !f.some(x => x.name === c.name));
      return next ? [...f.map(field => next.name === "phone" && field.name === "countryCode"
        ? { ...field, visible: true, required: next.required } : field), { ...next }] : f;
    });
  const removeField = (idx: number) => setPreChatFields((f) => f.filter((_, i) => i !== idx));
  const updateField = (idx: number, patch: Partial<PreChatField>) =>
    setPreChatFields((f) => {
      const next = f.map((fld, i) => i === idx ? { ...fld, ...patch } : fld);
      const changed = next[idx];
      if (changed.name === "phone" && changed.visible !== false) {
        const country = next.findIndex(field => field.name === "countryCode");
        if (country < 0) next.push({ ...CANONICAL_FIELDS[3], required: changed.required });
        else next[country] = { ...next[country], visible: true, required: changed.required };
      }
      return next;
    });

  if (isLoading) {
    return (
      <div className="p-6 flex items-center gap-2 text-muted-foreground">
        <Loader2 className="w-4 h-4 animate-spin" />Loading settings…
      </div>
    );
  }

  return (
    <div className="p-6 max-w-3xl mx-auto space-y-6" data-testid="page-live-chat-settings">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <SlidersHorizontal className="w-6 h-6" />
          <div>
            <h1 className="text-2xl font-bold">Live Chat Settings</h1>
            <p className="text-sm text-muted-foreground">Configure availability, routing, and visitor experience.</p>
          </div>
        </div>
        <Button
          onClick={() => saveMut.mutate()}
          disabled={saveMut.isPending}
          data-testid="btn-save-settings"
        >
          {saveMut.isPending ? (
            <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving…</>
          ) : (
            <><Save className="w-4 h-4 mr-2" />Save changes</>
          )}
        </Button>
      </div>

      {/* ── Office Hours ─────────────────────────────────────────────────── */}
      <Card data-testid="card-office-hours">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Clock className="w-4 h-4" />Office Hours
          </CardTitle>
          <CardDescription>
            When your team is available to accept live chats. Outside these hours visitors see the away message.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Timezone */}
          <div className="flex items-center gap-4">
            <Label className="w-28 shrink-0 text-xs font-semibold" htmlFor="lcs-timezone">Timezone</Label>
            <Select value={timezone} onValueChange={setTimezone}>
              <SelectTrigger id="lcs-timezone" className="w-64" data-testid="select-timezone">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {TIMEZONES.map((tz) => (
                  <SelectItem key={tz} value={tz}>{tz}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Day rows */}
          <div className="space-y-2">
            {officeHours.map((row, idx) => (
              <div key={row.day} className="flex items-center gap-3" data-testid={`row-hours-${row.day.toLowerCase()}`}>
                <Switch
                  checked={row.enabled}
                  onCheckedChange={() => toggleDay(idx)}
                  aria-label={`Enable ${row.day}`}
                  data-testid={`toggle-day-${row.day.toLowerCase()}`}
                />
                <span className="w-24 text-sm" aria-hidden="true">{row.day}</span>
                <Input
                  type="time"
                  value={row.open}
                  onChange={(e) => setHour(idx, "open", e.target.value)}
                  disabled={!row.enabled}
                  className="w-28"
                  aria-label={`${row.day} open time`}
                  data-testid={`input-open-${row.day.toLowerCase()}`}
                />
                <span className="text-muted-foreground text-xs" aria-hidden="true">to</span>
                <Input
                  type="time"
                  value={row.close}
                  onChange={(e) => setHour(idx, "close", e.target.value)}
                  disabled={!row.enabled}
                  className="w-28"
                  aria-label={`${row.day} close time`}
                  data-testid={`input-close-${row.day.toLowerCase()}`}
                />
                {!row.enabled && <Badge variant="outline" className="text-[10px]">Closed</Badge>}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* ── Messages ─────────────────────────────────────────────────────── */}
      <Card data-testid="card-messages">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <MessageSquare className="w-4 h-4" />Messages
          </CardTitle>
          <CardDescription>
            Greeting shown to visitors when your team is online or offline.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-1">
            <Label className="text-xs font-semibold" htmlFor="lcs-welcome-message">Welcome message</Label>
            <Textarea
              id="lcs-welcome-message"
              value={welcomeMessage}
              onChange={(e) => setWelcomeMessage(e.target.value)}
              rows={2}
              placeholder="Hello! How can I help you today?"
              data-testid="input-welcome-message"
            />
            <p className="text-xs text-muted-foreground">Shown when an operator is online and picks up the chat.</p>
          </div>
          <div className="space-y-1">
            <Label className="text-xs font-semibold" htmlFor="lcs-away-message">Away message</Label>
            <Textarea
              id="lcs-away-message"
              value={awayMessage}
              onChange={(e) => setAwayMessage(e.target.value)}
              rows={2}
              placeholder="We're currently away…"
              data-testid="input-away-message"
            />
            <p className="text-xs text-muted-foreground">Shown outside office hours or when no operator is online.</p>
          </div>
        </CardContent>
      </Card>

      {/* ── Routing ──────────────────────────────────────────────────────── */}
      <Card data-testid="card-routing">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Shuffle className="w-4 h-4" />Routing &amp; Capacity
          </CardTitle>
          <CardDescription>
            How incoming chats are assigned to operators and how many each can handle.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-4">
            <Label className="w-40 shrink-0 text-xs font-semibold" htmlFor="lcs-max-chats">Max chats per agent</Label>
            <Input
              id="lcs-max-chats"
              type="number"
              min={1}
              max={50}
              value={maxChats}
              onChange={(e) => setMaxChats(Math.max(1, Math.min(50, Number(e.target.value))))}
              className="w-24"
              data-testid="input-max-chats"
            />
          </div>
          <div className="flex items-center gap-4">
            <Label className="w-40 shrink-0 text-xs font-semibold" htmlFor="lcs-assignment-mode">Assignment mode</Label>
            <Select
              value={assignmentMode}
              onValueChange={setAssignmentMode}
            >
              <SelectTrigger id="lcs-assignment-mode" className="w-48" data-testid="select-assignment-mode">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="round_robin">Round robin</SelectItem>
                <SelectItem value="least_busy">Least busy</SelectItem>
                <SelectItem value="manual">Manual (operators self-claim)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <p className="text-xs text-muted-foreground">
            <strong>Round robin</strong> — distributes evenly across online operators.<br />
            <strong>Least busy</strong> — prefers the operator with fewest active chats.<br />
            <strong>Manual</strong> — chats sit in queue until an operator claims them.
          </p>
        </CardContent>
      </Card>

      {/* ── Pre-chat Fields ───────────────────────────────────────────────── */}
      <Card data-testid="card-prechat-fields">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <ClipboardList className="w-4 h-4" />Pre-chat Form Fields
          </CardTitle>
          <CardDescription>
            Configure canonical contact fields. Phone includes a country dialling code. Consent and the bot's pre-chat gate cannot be disabled. Empty settings use the legacy required contact form. Offline messages still require a friendly name and follow-up email.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {data?.preChatWarning && <p role="alert" className="text-sm text-destructive">{data.preChatWarning}</p>}
          {preChatFields.length === 0 && (
            <p className="text-sm text-muted-foreground italic">Using the required name, email, phone and consent defaults. Add fields to customize; omitted contact fields become hidden.</p>
          )}
          {preChatFields.some(f => !CANONICAL_FIELDS.some(c => c.name === f.name && c.type === f.type)) && (
            <p role="alert" className="text-sm text-destructive">Unsupported legacy fields are not collected. Remove or replace them with canonical contact fields before saving. Visitors use the safe default form until corrected.</p>
          )}
          {preChatFields.map((field, idx) => (
            <div key={idx} className="flex items-center gap-2 p-3 rounded-md border bg-muted/30" data-testid={`row-prechat-field-${idx}`}>
              <div className="flex-1 grid grid-cols-2 gap-2">
                <div className="space-y-1">
                  <Label className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wide" htmlFor={`lcs-field-label-${idx}`}>Label</Label>
                  <Input
                    id={`lcs-field-label-${idx}`}
                    value={field.label}
                    onChange={(e) => updateField(idx, { label: e.target.value })}
                    maxLength={80}
                    placeholder="e.g. Email address"
                    className="h-8 text-sm"
                    data-testid={`input-field-label-${idx}`}
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wide" htmlFor={`lcs-field-type-${idx}`}>Contact field</Label>
                  <Select value={field.name} onValueChange={(v) => updateField(idx, { ...CANONICAL_FIELDS.find(f => f.name === v)! })}>
                    <SelectTrigger id={`lcs-field-type-${idx}`} className="h-8 text-sm" data-testid={`select-field-type-${idx}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {CANONICAL_FIELDS.map((ft) => (
                        <SelectItem key={ft.name} value={ft.name} disabled={preChatFields.some((f, i) => i !== idx && f.name === ft.name)}>{ft.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <Label className="text-xs" htmlFor={`lcs-field-visible-${idx}`}>Visible</Label>
                <Switch id={`lcs-field-visible-${idx}`} checked={field.visible !== false} disabled={field.name === "consent" || (field.name === "countryCode" && preChatFields.some(f => f.name === "phone" && f.visible !== false))}
                  onCheckedChange={visible => updateField(idx, { visible, required: visible ? field.required : false })} />
                <Label className="text-xs text-muted-foreground" htmlFor={`lcs-field-required-${idx}`}>Required</Label>
                <Switch
                  id={`lcs-field-required-${idx}`}
                  checked={field.required}
                  disabled={field.visible === false || field.name === "consent" || (field.name === "countryCode" && preChatFields.some(f => f.name === "phone" && f.visible !== false && f.required))}
                  onCheckedChange={(v) => updateField(idx, { required: v })}
                  data-testid={`toggle-field-required-${idx}`}
                />
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 text-rose-500 hover:text-rose-600"
                  onClick={() => removeField(idx)}
                  disabled={field.name === "consent"}
                  aria-label={`Remove pre-chat field ${idx + 1}`}
                  data-testid={`btn-remove-field-${idx}`}
                >
                  <Trash2 className="w-3.5 h-3.5" aria-hidden="true" />
                </Button>
              </div>
            </div>
          ))}
          <Button
            variant="outline"
            size="sm"
            onClick={addField}
            data-testid="btn-add-field"
          >
            <Plus className="w-3.5 h-3.5 mr-1.5" />Add field
          </Button>
        </CardContent>
      </Card>

      {/* ── Post-chat Rating ─────────────────────────────────────────────── */}
      <Card data-testid="card-post-chat-rating">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Star className="w-4 h-4" />Post-chat Rating
          </CardTitle>
          <CardDescription>
            Ask visitors to rate their experience after the chat ends.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-3">
            <Switch
              checked={enablePostChatRating}
              onCheckedChange={setEnablePostChatRating}
              data-testid="toggle-post-chat-rating"
            />
            <Label className="text-sm">
              {enablePostChatRating ? "Enabled — visitors will see a rating prompt after each chat" : "Disabled"}
            </Label>
          </div>
        </CardContent>
      </Card>

      {/* Bottom save */}
      <div className="flex justify-end pb-6">
        <Button
          onClick={() => saveMut.mutate()}
          disabled={saveMut.isPending}
          data-testid="btn-save-settings-bottom"
        >
          {saveMut.isPending ? (
            <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving…</>
          ) : (
            <><Save className="w-4 h-4 mr-2" />Save changes</>
          )}
        </Button>
      </div>
    </div>
  );
}
